import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../common/SEO';
import Layout from '../common/Layout';
import PageHero, { HeroCtas } from '../components/ui/PageHero';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import { SectionHead } from '../components/ui/SectionHead';
import Notice from '../components/ui/Notice';
import StageList from '../components/ui/StageList';
import Button from '../components/ui/Button';
import {
  CriteriaSchedule,
  DecisionStops,
  LensList,
  RoleMatrix,
  MaterialPair,
  ReadinessList,
  PillarClose,
} from '../components/ui/PillarBlocks';
import { serviceSchema, faqSchema, breadcrumbSchema } from '../components/ui/schema';

const InnovationVentures = () => {
  const { t, i18n } = useTranslation('cy');
  const meta = t('innovation.meta', { returnObjects: true });
  const faqItems = t('innovation.faq.items', { returnObjects: true });

  const validationItems = t('innovation.validation.rows', { returnObjects: true }).map((r) => ({
    title: r.dim,
    fields: [
      { label: t('ui.cols.what'), value: r.what },
      { label: t('ui.cols.evidenceExamples'), value: r.evidence },
    ],
  }));

  const schema = [
    serviceSchema(t('nav.pillars.innovation.label'), meta.description),
    faqSchema(faqItems),
    breadcrumbSchema(
      [{ label: t('nav.home'), to: '/' }, { label: t('nav.pillars.innovation.label'), to: '/innovation-ventures' }],
      i18n.language
    ),
  ];

  return (
    <Layout>
      <SEO title={meta.title} description={meta.description} path="/innovation-ventures" schema={schema} />

      <PageHero
        id="innovation-hero"
        eyebrow={t('innovation.hero.eyebrow')}
        title={t('innovation.hero.h1')}
        kicker={t('innovation.hero.kicker')}
        breadcrumb={<Breadcrumbs items={[{ label: t('nav.pillars.innovation.label') }]} />}
      >
        <Notice variant="inline" className="cy-hero__boundary">
          {t('innovation.hero.boundary')} {t('innovation.hero.roleNote')}
        </Notice>
        <HeroCtas
          primary={t('innovation.hero.cta')}
          primaryTo="/submit-opportunity?type=innovation"
          secondary={t('innovation.hero.seeCriteria')}
          secondaryTo="#innovation-themes"
        />
      </PageHero>

      <section className="cy-section cy-section--alt cy-zone--doc" id="innovation-themes">
        <div className="cy-container">
          <SectionHead title={t('innovation.themes.h2')} lead={t('innovation.themes.lead')} />
          <CriteriaSchedule
            rows={t('innovation.themes.rows', { returnObjects: true })}
            term="problem"
            primary="tech"
            secondary="result"
          />
          <p className="cy-body cy-section-note">{t('innovation.themes.exclude')}</p>
        </div>
      </section>

      <section className="cy-section cy-zone--doc" id="innovation-not-fit">
        <div className="cy-container">
          <SectionHead title={t('innovation.notFit.h2')} lead={t('innovation.notFit.lead')} />
          <DecisionStops items={t('innovation.notFit.items', { returnObjects: true })} featured />
          <Notice variant="compliance" className="cy-notice--inline cy-section-note">
            {t('innovation.notFit.boundary')}
          </Notice>
          <p className="cy-section-action">
            <Link className="cy-link" to="/#home-pillars">{t('innovation.notFit.alt')}</Link>
          </p>
        </div>
      </section>

      <section className="cy-section cy-section--alt cy-zone--doc" id="innovation-readiness">
        <div className="cy-container">
          <SectionHead title={t('innovation.readiness.h2')} lead={t('innovation.readiness.lead')} />
          <ReadinessList
            rows={t('innovation.readiness.rows', { returnObjects: true })}
            evidenceLabel={t('ui.cols.evidenceExpected')}
            investLabel={t('ui.cols.invest')}
            pilotLabel={t('ui.cols.pilot')}
          />
          <p className="cy-fineprint cy-section-note">{t('innovation.readiness.lens')}</p>
          <Notice variant="inline" className="cy-section-note">{t('innovation.readiness.note')}</Notice>
        </div>
      </section>

      <section className="cy-section cy-zone--doc" id="innovation-validation">
        <div className="cy-container">
          <SectionHead eyebrow={t('innovation.validation.eyebrow')} title={t('innovation.validation.h2')} lead={t('innovation.validation.lead')} />
          <LensList items={validationItems} />
          <p className="cy-fineprint cy-section-note">{t('innovation.validation.lens')}</p>
          <Notice variant="info" className="cy-section-note">{t('innovation.validation.ai')}</Notice>
          <p className="cy-body cy-section-note">
            {t('innovation.validation.gateNote')}{' '}
            <Link className="cy-link" to="/approach#approach-gates">{t('innovation.validation.gateLink')}</Link>
          </p>
        </div>
      </section>

      <section className="cy-section cy-section--alt cy-zone--doc" id="innovation-pathways">
        <div className="cy-container">
          <SectionHead title={t('innovation.pathways.h2')} lead={t('innovation.pathways.lead')} />
          <RoleMatrix
            rows={t('innovation.pathways.rows', { returnObjects: true })}
            titleKey="path"
            fields={[
              { key: 'precondition', label: t('ui.cols.precondition') },
              { key: 'role', label: t('ui.cols.cyRole') },
            ]}
            notKey="notPromise"
            notLabel={t('ui.cols.notPromise')}
          />
          <Notice variant="compliance" className="cy-section-note">{t('innovation.pathways.note')}</Notice>
          <p className="cy-section-action">
            <Link className="cy-link" to="/approach#approach-roles">{t('innovation.pathways.cta')}</Link>
          </p>
        </div>
      </section>

      <section className="cy-section cy-zone--doc" id="innovation-materials">
        <div className="cy-container">
          <SectionHead title={t('innovation.materials.h2')} lead={t('innovation.materials.lead')} />
          <MaterialPair
            minimumTitle={t('innovation.materials.minimumTitle')}
            laterTitle={t('innovation.materials.laterTitle')}
            minimum={t('innovation.materials.minimum', { returnObjects: true })}
            later={t('innovation.materials.later', { returnObjects: true })}
          />
          <Notice variant="inline" className="cy-section-note">{t('innovation.materials.privacy')}</Notice>
          <div className="cy-section-action">
            <Button to="/submit-opportunity?type=innovation">{t('innovation.materials.cta')}</Button>
          </div>
        </div>
      </section>

      <section className="cy-section cy-section--alt cy-zone--doc" id="innovation-process">
        <div className="cy-container">
          <SectionHead title={t('innovation.process.h2')} lead={t('innovation.process.lead')} />
          <StageList items={t('innovation.process.steps', { returnObjects: true })} />
          <p className="cy-fineprint cy-section-note">{t('innovation.process.note')}</p>
          <p className="cy-section-action">
            <Link className="cy-link" to="/approach#approach-stages">{t('innovation.process.cta')}</Link>
          </p>
        </div>
      </section>

      <PillarClose
        faqId="innovation-faq"
        ctaId="innovation-faq-cta"
        faqTitle={t('innovation.faq.h2')}
        faqItems={faqItems}
        idPrefix="innovation-faq"
        cta={t('innovation.faq.cta')}
        ctaTo="/submit-opportunity?type=innovation"
        alt={t('innovation.faq.alt')}
        altTo="/contact?topic=partnership#contact-form"
        siblings={t('innovation.faq.siblings', { returnObjects: true })}
      />
    </Layout>
  );
};

export default InnovationVentures;
